import { useState, type FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../../components/liks/LiksDialog/LiksDialog';
import { LiksTextFormField } from '../../../components/liks/LiksTextFormField/LiksTextFormField';
import { useI18n } from '../../../i18n/useI18n';
import { isPasswordPolicyCompliant } from '../../../utils/passwordPolicyUtils';
import { PasswordPolicyChecklist } from './PasswordPolicyChecklist';
import './ChangePasswordDialog.css';

type ChangePasswordDialogProps = {
  onSubmit: (currentPassword: string, newPassword: string) => void;
  onClose: () => void;
  isSubmitting?: boolean;
};

export function ChangePasswordDialog({
  onSubmit,
  onClose,
  isSubmitting = false,
}: ChangePasswordDialogProps) {
  const { translate } = useI18n();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const trimmedCurrentPassword = currentPassword.trim();
  const trimmedNewPassword = newPassword.trim();
  const trimmedConfirmPassword = confirmPassword.trim();
  const isPolicyCompliant = isPasswordPolicyCompliant(trimmedNewPassword);
  const passwordsMatch =
    trimmedNewPassword.length > 0 &&
    trimmedNewPassword === trimmedConfirmPassword;
  const confirmMismatch =
    trimmedConfirmPassword.length > 0 &&
    trimmedNewPassword !== trimmedConfirmPassword;
  const isSubmitDisabled =
    isSubmitting ||
    trimmedCurrentPassword.length === 0 ||
    !isPolicyCompliant ||
    !passwordsMatch;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitDisabled) {
      return;
    }
    onSubmit(trimmedCurrentPassword, trimmedNewPassword);
  };

  return (
    <LiksDialog
      isOpen
      as="form"
      size="sm"
      title={translate('settings.password.dialogTitle')}
      onClose={onClose}
      onSubmit={handleSubmit}
      closeDisabled={isSubmitting}
      isDismissDisabled={isSubmitting}
      footer={
        <>
          <LiksButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {translate('settings.password.cancel')}
          </LiksButton>
          <LiksButton type="submit" disabled={isSubmitDisabled}>
            {translate('settings.password.save')}
          </LiksButton>
        </>
      }
    >
      <div className="change-password-dialog__fields">
        <LiksTextFormField
          label={translate('settings.password.currentLabel')}
          required
          type={showCurrentPassword ? 'text' : 'password'}
          value={currentPassword}
          onChange={setCurrentPassword}
          autoFocus
          disabled={isSubmitting}
          autoComplete="current-password"
          trailing={
            <button
              type="button"
              className="liks-text-form-field__trailing-button"
              aria-label={translate(
                showCurrentPassword
                  ? 'settings.password.hidePassword'
                  : 'settings.password.showPassword',
              )}
              onClick={() => setShowCurrentPassword((current) => !current)}
              disabled={isSubmitting}
            >
              {showCurrentPassword ? (
                <EyeOff strokeWidth={2} aria-hidden />
              ) : (
                <Eye strokeWidth={2} aria-hidden />
              )}
            </button>
          }
        />

        <LiksTextFormField
          label={translate('settings.password.newLabel')}
          required
          type={showNewPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={setNewPassword}
          disabled={isSubmitting}
          autoComplete="new-password"
          trailing={
            <button
              type="button"
              className="liks-text-form-field__trailing-button"
              aria-label={translate(
                showNewPassword
                  ? 'settings.password.hidePassword'
                  : 'settings.password.showPassword',
              )}
              onClick={() => setShowNewPassword((current) => !current)}
              disabled={isSubmitting}
            >
              {showNewPassword ? (
                <EyeOff strokeWidth={2} aria-hidden />
              ) : (
                <Eye strokeWidth={2} aria-hidden />
              )}
            </button>
          }
        />

        <LiksTextFormField
          label={translate('settings.password.confirmLabel')}
          required
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={setConfirmPassword}
          disabled={isSubmitting}
          autoComplete="new-password"
          error={
            confirmMismatch
              ? translate('settings.password.confirmMismatch')
              : null
          }
          trailing={
            <button
              type="button"
              className="liks-text-form-field__trailing-button"
              aria-label={translate(
                showConfirmPassword
                  ? 'settings.password.hidePassword'
                  : 'settings.password.showPassword',
              )}
              onClick={() => setShowConfirmPassword((current) => !current)}
              disabled={isSubmitting}
            >
              {showConfirmPassword ? (
                <EyeOff strokeWidth={2} aria-hidden />
              ) : (
                <Eye strokeWidth={2} aria-hidden />
              )}
            </button>
          }
        />
      </div>

      <PasswordPolicyChecklist
        password={trimmedNewPassword}
        policyKeyPrefix="settings.password.policy"
      />
    </LiksDialog>
  );
}
